import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IColumnSelectionState, ITableColumn } from '../types';

const normalizeColumnSelections = (
  allColumns: ITableColumn[],
  columnSelections: IColumnSelectionState[]
): IColumnSelectionState[] => {
  const availableAccessors = new Set(
    allColumns.map((column: ITableColumn) => column.accessor)
  );

  const preservedSelections = columnSelections
    .filter((columnSelection: IColumnSelectionState) =>
      availableAccessors.has(columnSelection.accessor)
    )
    .sort(
      (left: IColumnSelectionState, right: IColumnSelectionState) =>
        left.position - right.position
    )
    .map((columnSelection: IColumnSelectionState, position: number) => ({
      ...columnSelection,
      position,
    }));

  const preservedAccessors = new Set(
    preservedSelections.map((columnSelection) => columnSelection.accessor)
  );

  const appendedSelections = allColumns
    .filter((column: ITableColumn) => !preservedAccessors.has(column.accessor))
    .map((column: ITableColumn, index: number) => ({
      accessor: column.accessor,
      position: preservedSelections.length + index,
      selected:
        column.columnDefaultSelection ||
        false ||
        !!column.columnSelectionDisabled,
    }));

  return [...preservedSelections, ...appendedSelections];
};

interface IUseColumnSelectionDialogArgs {
  allColumns: ITableColumn[];
  selectedColumns: IColumnSelectionState[];
  onChange: (selectedColumns: IColumnSelectionState[]) => void;
}

export const useColumnSelectionDialog = ({
  allColumns,
  selectedColumns,
  onChange,
}: IUseColumnSelectionDialogArgs) => {
  const [orderedColumnSelections, setOrderedColumnSelections] = useState<
    IColumnSelectionState[]
  >(() => normalizeColumnSelections(allColumns, selectedColumns));
  const [draggedAccessor, setDraggedAccessor] = useState<string | undefined>();

  const preDragSelectionsRef = useRef<IColumnSelectionState[] | null>(null);
  const didDropRef = useRef(false);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const snapshotRef = useRef<Map<string, number> | null>(null);

  const setItemRef = (accessor: string) => (el: HTMLElement | null) => {
    if (el) {
      itemRefs.current.set(accessor, el);
    } else {
      itemRefs.current.delete(accessor);
    }
  };

  const resetColumnsToDefault = () => {
    const defaults = allColumns.map((column: ITableColumn, index: number) => ({
      accessor: column.accessor,
      position: index,
      selected: column.columnDefaultSelection || !!column.columnSelectionDisabled,
    }));

    setOrderedColumnSelections(defaults);
    onChange(defaults);
  };

  const snapshotPositions = () => {
    const snapshot = new Map<string, number>();

    itemRefs.current.forEach((el: HTMLElement, accessor: string) => {
      snapshot.set(accessor, el.getBoundingClientRect().top);
    });

    snapshotRef.current = snapshot;
  };

  useLayoutEffect(() => {
    const snapshot: Map<string, number> = snapshotRef.current;

    if (!snapshot) {
      return;
    }

    snapshotRef.current = null;

    itemRefs.current.forEach((el: HTMLElement, accessor: string) => {
      if (accessor === draggedAccessor) {
        return;
      }

      const oldTop: number = snapshot.get(accessor);

      if (oldTop === undefined) {
        return;
      }

      const delta: number = oldTop - el.getBoundingClientRect().top;

      if (delta === 0) {
        return;
      }

      el.style.transition = 'none';
      el.style.transform = `translateY(${delta}px)`;
      el.getBoundingClientRect(); // force reflow
      el.style.transition =
        'transform 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      el.style.transform = '';
    });
  }, [orderedColumnSelections]);

  useEffect(() => {
    setOrderedColumnSelections((currentSelections: IColumnSelectionState[]) => {
      const nextOrderedSelections: IColumnSelectionState[] =
        normalizeColumnSelections(
          allColumns,
          selectedColumns.length ? selectedColumns : currentSelections
        );

      if (
        currentSelections.length === nextOrderedSelections.length &&
        currentSelections.every(
          (columnSelection: IColumnSelectionState, index: number) =>
            columnSelection.accessor ===
              nextOrderedSelections[index]?.accessor &&
            columnSelection.position ===
              nextOrderedSelections[index]?.position &&
            columnSelection.selected === nextOrderedSelections[index]?.selected
        )
      ) {
        return currentSelections;
      }

      return nextOrderedSelections;
    });
  }, [allColumns, selectedColumns]);

  const orderedColumns: ITableColumn[] = useMemo(() => {
    const columnsByAccessor = new Map(
      allColumns.map((column) => [column.accessor, column])
    );

    return orderedColumnSelections
      .map((columnSelection: IColumnSelectionState) =>
        columnsByAccessor.get(columnSelection.accessor)
      )
      .filter(Boolean) as ITableColumn[];
  }, [allColumns, orderedColumnSelections]);

  const selectedAccessors = useMemo(
    () =>
      new Set(
        orderedColumnSelections
          .filter(
            (columnSelection: IColumnSelectionState) => columnSelection.selected
          )
          .map(
            (columnSelection: IColumnSelectionState) => columnSelection.accessor
          )
      ),
    [orderedColumnSelections]
  );

  // --- Drag and Drop Logic ---
  const startDrag = (accessor: string) => {
    preDragSelectionsRef.current = orderedColumnSelections;
    didDropRef.current = false;
    setDraggedAccessor(accessor);
  };

  const hoverColumn = (targetColumn: ITableColumn) => {
    if (!draggedAccessor || draggedAccessor === targetColumn.accessor) {
      return;
    }

    const draggedIndex: number = orderedColumnSelections.findIndex(
      (c: IColumnSelectionState) => c.accessor === draggedAccessor
    );

    const targetIndex: number = orderedColumnSelections.findIndex(
      (c: IColumnSelectionState) => c.accessor === targetColumn.accessor
    );

    if (draggedIndex < 0 || targetIndex < 0) {
      return;
    }

    snapshotPositions();

    const next: IColumnSelectionState[] = [...orderedColumnSelections];
    const [moved] = next.splice(draggedIndex, 1);

    next.splice(targetIndex, 0, moved);
    setOrderedColumnSelections(
      next.map((c: IColumnSelectionState, position: number) => ({
        ...c,
        position,
      }))
    );
  };

  const dropColumn = () => {
    didDropRef.current = true;
    preDragSelectionsRef.current = null;
    onChange(orderedColumnSelections);
    setDraggedAccessor(undefined);
  };

  const endDrag = () => {
    if (!didDropRef.current && preDragSelectionsRef.current) {
      snapshotPositions();
      setOrderedColumnSelections(preDragSelectionsRef.current);
    }

    preDragSelectionsRef.current = null;
    didDropRef.current = false;
    setDraggedAccessor(undefined);
  };
  // --- End Drag and Drop Logic ---

  const toggleColumn = (column: ITableColumn) => {
    if (column.columnSelectionDisabled) {
      return;
    }

    const nextColumnSelections: IColumnSelectionState[] =
      orderedColumnSelections.map((columnSelection: IColumnSelectionState) => {
        if (columnSelection.accessor !== column.accessor) {
          return columnSelection;
        }

        return {
          ...columnSelection,
          selected: !columnSelection.selected,
        };
      });

    setOrderedColumnSelections(nextColumnSelections);
    onChange(nextColumnSelections);
  };

  return {
    draggedAccessor,
    orderedColumns,
    selectedAccessors,
    setItemRef,
    toggleColumn,
    startDrag,
    hoverColumn,
    dropColumn,
    endDrag,
    resetColumnsToDefault,
  };
};
